import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { 
  FileText, 
  Plus, 
  Upload,
  Eye, 
  Edit, 
  MoreVertical,
  Filter,
  Search,
  CheckCircle,
  XCircle,
  Settings,
  BarChart3,
  FileImage,
  FileCheck,
  Heart,
  UserCheck,
  GraduationCap,
  AlertTriangle
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { supabase } from '@/lib/supabase';
import { AdminPageLayout } from '@/components/admin/AdminPageLayout';
import { UploadButton } from '@/components/common/UploadButton';
import CreateDocumentRequestModal from '@/components/admin/CreateDocumentRequestModal';
import TemplateDesignerModal from '@/components/admin/TemplateDesignerModal';

// Types
interface FormTemplate {
  id: string;
  organization_id: string;
  name: string;
  description?: string;
  category: TemplateCategory;
  template_file_url: string;
  is_active: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
  // Computed fields
  usage_count?: number;
  completion_rate?: number;
  last_used?: string;
}

type TemplateCategory = 'waiver' | 'medical' | 'emergency' | 'enrollment' | 'media' | 'other';

// Template Preview Component with signed URL support
const TemplatePreviewFrame: React.FC<{ templateUrl: string }> = ({ templateUrl }) => {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const getSignedUrl = async () => {
      setLoading(true);
      setError(null);

      // If URL contains Supabase storage, create signed URL
      if (templateUrl.includes('.supabase.co/storage/v1/object/')) {
        try {
          // Extract bucket and file path from Supabase URL
          let bucket = '';
          let filePath = '';
          
          if (templateUrl.includes('/storage/v1/object/public/')) {
            const urlParts = templateUrl.split('/storage/v1/object/public/');
            if (urlParts.length === 2) {
              const pathParts = urlParts[1].split('/');
              bucket = pathParts[0];
              filePath = pathParts.slice(1).join('/');
            }
          } else {
            // Assume it's a private bucket that needs signing
            const urlParts = templateUrl.split('/storage/v1/object/');
            if (urlParts.length === 2) {
              const pathParts = urlParts[1].split('/');
              bucket = pathParts[0];
              filePath = pathParts.slice(1).join('/');
            }
          }

          if (bucket && filePath) {
            const { data: signedData, error: signError } = await supabase.storage
              .from(bucket)
              .createSignedUrl(filePath, 3600); // 1 hour

            if (signError) {
              console.error('Failed to create signed URL:', signError);
              setError('Failed to load document preview');
            } else {
              setSignedUrl(signedData.signedUrl);
            }
          } else {
            setError('Invalid document URL format');
          }
        } catch (err) {
          console.error('Error creating signed URL:', err);
          setError('Failed to create preview URL');
        }
      } else {
        // Use URL directly if not Supabase storage
        setSignedUrl(templateUrl);
      }

      setLoading(false);
    };

    getSignedUrl();
  }, [templateUrl]);

  if (loading) {
    return (
      <div className="border rounded-lg p-4 h-96 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-2"></div>
          <p className="text-gray-600">Loading preview...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="border rounded-lg p-4 h-96 flex items-center justify-center">
        <div className="text-center">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-2" />
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="border rounded-lg p-4">
      <iframe
        src={signedUrl || ''}
        className="w-full h-96 border-0"
        title="Template Preview"
      />
    </div>
  );
};

const categoryConfig = {
  waiver: {
    label: 'Liability Waivers',
    icon: AlertTriangle,
    color: 'bg-red-100 text-red-800',
    description: 'Liability waivers and safety agreements'
  },
  medical: {
    label: 'Medical Forms',
    icon: Heart,
    color: 'bg-pink-100 text-pink-800',
    description: 'Medical information and health forms'
  },
  emergency: {
    label: 'Emergency Contacts',
    icon: UserCheck,
    color: 'bg-orange-100 text-orange-800',
    description: 'Emergency contact information'
  },
  enrollment: {
    label: 'Enrollment Forms',
    icon: GraduationCap,
    color: 'bg-blue-100 text-blue-800',
    description: 'Registration and enrollment forms'
  },
  media: {
    label: 'Media Release',
    icon: FileImage,
    color: 'bg-purple-100 text-purple-800',
    description: 'Photo and video release forms'
  },
  other: {
    label: 'Other Forms',
    icon: FileText,
    color: 'bg-gray-100 text-gray-800',
    description: 'Miscellaneous forms and documents'
  }
};

const FormTemplates = () => {
  const { user } = useAuth();
  const organizationId = user?.user_metadata?.organization_id;
  
  // State
  const [templates, setTemplates] = useState<FormTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<TemplateCategory | 'all'>('all');
  const [selectedTemplate, setSelectedTemplate] = useState<FormTemplate | null>(null);
  
  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isStatsModalOpen, setIsStatsModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);
  const [isTemplateDesignerOpen, setIsTemplateDesignerOpen] = useState(false);
  const [templateToEdit, setTemplateToEdit] = useState<FormTemplate | undefined>();
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'waiver' as TemplateCategory,
    template_file_url: ''
  });
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);

  // Fetch templates function
  const fetchTemplates = useCallback(async () => {
    if (!organizationId) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('form_templates')
        .select(`
          *,
          created_by_profile:user_profiles!form_templates_created_by_fkey(first_name, last_name)
        `)
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Get usage statistics for each template
      const templatesWithStats = await Promise.all(
        (data || []).map(async (template) => {
          try {
            const { data: statsData } = await supabase
              .rpc('get_form_template_stats', { template_uuid: template.id });
            
            const stats = statsData?.[0] || { total_requests: 0, completion_rate: 0 };
            
            return {
              ...template,
              usage_count: stats.total_requests,
              completion_rate: stats.completion_rate
            };
          } catch (error) {
            console.error('Error fetching template stats:', error);
            return {
              ...template,
              usage_count: 0,
              completion_rate: 0
            };
          }
        })
      );

      setTemplates(templatesWithStats);
    } catch (error) {
      console.error('Error fetching form templates:', error);
      toast.error('Failed to load form templates');
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  // Fetch templates on mount
  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Filter templates
  const filteredTemplates = templates.filter(template => {
    // Filter by category
    if (selectedCategory !== 'all' && template.category !== selectedCategory) {
      return false;
    }
    
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        template.name.toLowerCase().includes(query) ||
        template.description?.toLowerCase().includes(query) ||
        template.category.toLowerCase().includes(query)
      );
    }
    
    return true;
  });

  // Handle file upload
  const handleFileUpload = async (file: File) => {
    if (!organizationId) {
      toast.error('Organization not found');
      return;
    }

    // Validate file type
    const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only PDF and DOCX files are allowed');
      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress(0);

      // Sanitize filename - remove special characters and multiple spaces
      const sanitizedName = file.name
        .replace(/[^a-zA-Z0-9.-]/g, '_') // Replace special chars with underscore
        .replace(/_+/g, '_') // Replace multiple underscores with single
        .replace(/^_|_$/g, ''); // Remove leading/trailing underscores

      // Upload to Supabase storage
      const fileName = `${Date.now()}-${sanitizedName}`;
      const filePath = `${organizationId}/templates/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('parent-documents')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('parent-documents')
        .getPublicUrl(filePath);

      setFormData(prev => ({ ...prev, template_file_url: publicUrl }));
      setUploadProgress(100);
      toast.success('Template file uploaded successfully');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload template file');
    } finally {
      setTimeout(() => {
        setIsUploading(false);
        setUploadProgress(null);
      }, 1000);
    }
  };

  // Update template
  const handleUpdateTemplate = async () => {
    if (!organizationId || !selectedTemplate) {
      toast.error('Template information not available');
      return;
    }

    if (!formData.name || !formData.template_file_url) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const { data, error } = await supabase
        .from('form_templates')
        .update({
          name: formData.name,
          description: formData.description,
          category: formData.category,
          template_file_url: formData.template_file_url
        })
        .eq('id', selectedTemplate.id)
        .select()
        .single();

      if (error) throw error;

      setTemplates(prev => 
        prev.map(t => 
          t.id === selectedTemplate.id 
            ? { ...data, usage_count: t.usage_count, completion_rate: t.completion_rate }
            : t
        )
      );
      setIsEditModalOpen(false);
      setSelectedTemplate(null);
      setFormData({ name: '', description: '', category: 'waiver', template_file_url: '' });
      toast.success('Template updated successfully');
    } catch (error) {
      console.error('Error updating template:', error);
      toast.error('Failed to update template');
    }
  };

  // Create template
  const handleCreateTemplate = async () => {
    if (!organizationId || !user?.id) {
      toast.error('User information not available');
      return;
    }

    if (!formData.name || !formData.template_file_url) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const { data, error } = await supabase
        .from('form_templates')
        .insert({
          organization_id: organizationId,
          name: formData.name,
          description: formData.description,
          category: formData.category,
          template_file_url: formData.template_file_url,
          created_by: user.id
        })
        .select()
        .single();

      if (error) throw error;

      setTemplates(prev => [{ ...data, usage_count: 0, completion_rate: 0 }, ...prev]);
      setIsCreateModalOpen(false);
      setFormData({ name: '', description: '', category: 'waiver', template_file_url: '' });
      toast.success('Template created successfully');
    } catch (error) {
      console.error('Error creating template:', error);
      toast.error('Failed to create template');
    }
  };

  // Toggle template active status
  const handleToggleActive = async (template: FormTemplate) => {
    try {
      const { error } = await supabase
        .from('form_templates')
        .update({ is_active: !template.is_active })
        .eq('id', template.id);

      if (error) throw error;

      setTemplates(prev => 
        prev.map(t => 
          t.id === template.id 
            ? { ...t, is_active: !t.is_active }
            : t
        )
      );

      toast.success(`Template ${!template.is_active ? 'activated' : 'deactivated'}`);
    } catch (error) {
      console.error('Error toggling template status:', error);
      toast.error('Failed to update template status');
    }
  };

  // Category statistics
  const categoryStats = Object.entries(categoryConfig).map(([key, config]) => {
    const categoryTemplates = templates.filter(t => t.category === key);
    const activeCount = categoryTemplates.filter(t => t.is_active).length;
    const totalUsage = categoryTemplates.reduce((sum, t) => sum + (t.usage_count || 0), 0);
    
    return {
      category: key as TemplateCategory,
      config,
      count: categoryTemplates.length,
      activeCount,
      totalUsage
    };
  });

  return (
    <AdminPageLayout
      title="Form Templates"
      description="Manage electronic form templates for automated document requests"
      icon={FileText}
      actions={
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              New Template
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setIsTemplateDesignerOpen(true)}>
              <Edit className="h-4 w-4 mr-2" />
              Create with Form Designer
              <Badge variant="secondary" className="ml-2">New</Badge>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setIsCreateModalOpen(true)}>
              <Upload className="h-4 w-4 mr-2" />
              Upload Existing Template
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      }
    >
      {/* Category Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {categoryStats.map(({ category, config, count, activeCount, totalUsage }) => {
          const Icon = config.icon;
          return (
            <Card 
              key={category} 
              className={cn(
                "cursor-pointer transition-all hover:shadow-md",
                selectedCategory === category && "ring-2 ring-primary"
              )}
              onClick={() => setSelectedCategory(selectedCategory === category ? 'all' : category)}
            >
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className={cn("p-3 rounded-lg", config.color.replace('text-', 'text-').replace('bg-', 'bg-'))}>
                    <Icon className="h-6 w-6" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{config.label}</h3>
                    <p className="text-sm text-gray-500">{count} templates</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-gray-900">{activeCount}</p>
                    <p className="text-xs text-gray-500">active</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filters and Search */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full"
              />
            </div>
            
            <div className="w-full md:w-64">
              <Select value={selectedCategory} onValueChange={(value) => setSelectedCategory(value as TemplateCategory | 'all')}>
                <SelectTrigger>
                  <div className="flex items-center">
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Filter by category" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {Object.entries(categoryConfig).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      {config.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Templates Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="py-10 text-center">
              <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium mb-2">No Templates Found</h3>
              <p className="text-muted-foreground mb-6">
                {searchQuery || selectedCategory !== 'all'
                  ? "Try adjusting your filters"
                  : "Create your first form template to get started"}
              </p>
              {!searchQuery && selectedCategory === 'all' && (
                <Button onClick={() => setIsCreateModalOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Template
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Template</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Usage</TableHead>
                  <TableHead>Completion Rate</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTemplates.map((template) => {
                  const categoryInfo = categoryConfig[template.category];
                  const CategoryIcon = categoryInfo.icon;
                  
                  return (
                    <TableRow key={template.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className={cn("p-2 rounded-md", categoryInfo.color)}>
                            <CategoryIcon className="h-4 w-4" />
                          </div>
                          <div>
                            <p className="font-medium">{template.name}</p>
                            {template.description && (
                              <p className="text-sm text-muted-foreground">
                                {template.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={categoryInfo.color}>
                          {categoryInfo.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={template.is_active ? "default" : "secondary"}>
                          {template.is_active ? (
                            <>
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Active
                            </>
                          ) : (
                            <>
                              <XCircle className="h-3 w-3 mr-1" />
                              Inactive
                            </>
                          )}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="font-medium">{template.usage_count || 0}</span>
                        <span className="text-muted-foreground ml-1">requests</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Progress 
                            value={template.completion_rate || 0} 
                            className="w-16" 
                          />
                          <span className="text-sm font-medium">
                            {template.completion_rate || 0}%
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {format(new Date(template.created_at), 'MMM d, yyyy')}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => {
                              setSelectedTemplate(template);
                              setIsPreviewModalOpen(true);
                            }}>
                              <Eye className="h-4 w-4 mr-2" />
                              Preview
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => {
                              setSelectedTemplate(template);
                              setIsSendModalOpen(true);
                            }}>
                              <Plus className="h-4 w-4 mr-2" />
                              Send to Families
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => {
                              setSelectedTemplate(template);
                              setIsStatsModalOpen(true);
                            }}>
                              <BarChart3 className="h-4 w-4 mr-2" />
                              View Stats
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => {
                              setTemplateToEdit(template);
                              setIsTemplateDesignerOpen(true);
                            }}>
                              <Settings className="h-4 w-4 mr-2" />
                              Edit in Form Designer
                              <Badge variant="secondary" className="ml-2">New</Badge>
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => {
                              setSelectedTemplate(template);
                              setFormData({
                                name: template.name,
                                description: template.description || '',
                                category: template.category,
                                template_file_url: template.template_file_url
                              });
                              setIsEditModalOpen(true);
                            }}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleToggleActive(template)}>
                              {template.is_active ? (
                                <>
                                  <XCircle className="h-4 w-4 mr-2" />
                                  Deactivate
                                </>
                              ) : (
                                <>
                                  <CheckCircle className="h-4 w-4 mr-2" />
                                  Activate
                                </>
                              )}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Template Preview Modal */}
      <Dialog open={isPreviewModalOpen} onOpenChange={setIsPreviewModalOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Template Preview - {selectedTemplate?.name}</DialogTitle>
          </DialogHeader>
          
          {selectedTemplate && (
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <div className={`p-3 rounded-lg ${categoryConfig[selectedTemplate.category].color}`}>
                  {React.createElement(categoryConfig[selectedTemplate.category].icon, { className: "h-6 w-6" })}
                </div>
                <div>
                  <h3 className="font-semibold">{selectedTemplate.name}</h3>
                  <p className="text-sm text-gray-600">{selectedTemplate.description}</p>
                  <Badge className={categoryConfig[selectedTemplate.category].color}>
                    {categoryConfig[selectedTemplate.category].label}
                  </Badge>
                </div>
              </div>
              
              <TemplatePreviewFrame templateUrl={selectedTemplate.template_file_url} />
              
              <div className="flex justify-end gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => window.open(selectedTemplate.template_file_url, '_blank')}
                >
                  <Eye className="h-4 w-4 mr-2" />
                  Open Full View
                </Button>
                <Button onClick={() => setIsPreviewModalOpen(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Template Stats Modal */}
      <Dialog open={isStatsModalOpen} onOpenChange={setIsStatsModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Template Statistics - {selectedTemplate?.name}</DialogTitle>
          </DialogHeader>
          
          {selectedTemplate && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-blue-600">{selectedTemplate.usage_count || 0}</p>
                      <p className="text-sm text-gray-600">Total Requests</p>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600">{selectedTemplate.completion_rate || 0}%</p>
                      <p className="text-sm text-gray-600">Completion Rate</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Category:</span>
                  <Badge className={categoryConfig[selectedTemplate.category].color}>
                    {categoryConfig[selectedTemplate.category].label}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Status:</span>
                  <Badge variant={selectedTemplate.is_active ? "default" : "secondary"}>
                    {selectedTemplate.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Created:</span>
                  <span className="text-sm">{format(new Date(selectedTemplate.created_at), 'MMM d, yyyy')}</span>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button onClick={() => setIsStatsModalOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Template Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Template</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-name">Template Name</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Liability Waiver 2024"
              />
            </div>
            
            <div>
              <Label htmlFor="edit-category">Category</Label>
              <Select value={formData.category} onValueChange={(value) => setFormData(prev => ({ ...prev, category: value as TemplateCategory }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(categoryConfig).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      {config.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="edit-description">Description (Optional)</Label>
              <Textarea
                id="edit-description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe what this template is used for..."
                rows={3}
              />
            </div>
            
            <div>
              <Label>Template File</Label>
              <div className="space-y-2">
                {formData.template_file_url && (
                  <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                    <FileText className="h-4 w-4 text-gray-600" />
                    <span className="text-sm text-gray-600">Current file uploaded</span>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => window.open(formData.template_file_url, '_blank')}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                  </div>
                )}
                <UploadButton 
                  onUpload={handleFileUpload}
                  isUploading={isUploading}
                  progress={uploadProgress}
                  accept=".pdf,.docx"
                />
                <p className="text-sm text-muted-foreground">
                  Upload a new PDF or DOCX file to replace the current template
                </p>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setIsEditModalOpen(false);
                setSelectedTemplate(null);
                setFormData({ name: '', description: '', category: 'waiver', template_file_url: '' });
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleUpdateTemplate}
              disabled={!formData.name || !formData.template_file_url}
            >
              Update Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Template Modal */}
      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New Form Template</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Template Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Liability Waiver 2024"
              />
            </div>
            
            <div>
              <Label htmlFor="category">Category</Label>
              <Select value={formData.category} onValueChange={(value) => setFormData(prev => ({ ...prev, category: value as TemplateCategory }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(categoryConfig).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      {config.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="description">Description (Optional)</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe what this template is used for..."
                rows={3}
              />
            </div>
            
            <div>
              <Label>Template File</Label>
              <UploadButton 
                onUpload={handleFileUpload}
                isUploading={isUploading}
                progress={uploadProgress}
                accept=".pdf,.docx"
              />
              <p className="text-sm text-muted-foreground mt-1">
                Upload a PDF or DOCX file that will be used as the form template
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateTemplate}
              disabled={!formData.name || !formData.template_file_url}
            >
              Create Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Template Modal */}
      {selectedTemplate && (
        <CreateDocumentRequestModal
          open={isSendModalOpen}
          onOpenChange={setIsSendModalOpen}
          onSuccess={() => {
            setIsSendModalOpen(false);
            setSelectedTemplate(null);
            toast.success('Electronic document request sent successfully');
          }}
          preselectedTemplate={selectedTemplate}
        />
      )}

      {/* Template Designer Modal */}
      <TemplateDesignerModal
        open={isTemplateDesignerOpen}
        onOpenChange={setIsTemplateDesignerOpen}
        template={templateToEdit}
        onSuccess={useCallback(() => {
          setIsTemplateDesignerOpen(false);
          setTemplateToEdit(undefined);
          fetchTemplates(); // Refresh the templates list
          toast.success('Template saved successfully');
        }, [fetchTemplates])}
      />
    </AdminPageLayout>
  );
};

export default FormTemplates;